import { z } from 'zod';
import type { FunctionComponent } from 'react';
import {
  BathMd,
  BedMd,
  CarMd,
  FloorplanMd,
  HomeMd,
  LandSizeMd,
  MoneyMd,
  SoldMd,
  paletteHelper,
  spacingHelper,
  Text,
  radiusHelper,
} from '@property.com.au/ui-kit';
import styled, { css } from 'styled-components';
import placeholderSVG from '../../../../../../assets/property-image-placeholder.svg';
import {
  comparisonTableColumnSchema,
  comparisonTableComponentSchema,
} from '../hooks/uiComponentsSchemas';

const sortedComparisonTableKeys: Array<keyof z.infer<typeof comparisonTableColumnSchema>> = [
  'imageUrl',
  'address',
  'price',
  'propertyType',
  'bedrooms',
  'bathrooms',
  'carSpaces',
  'landSize',
  'buildingArea',
  'soldDate',
];

const headers: Partial<Record<keyof z.infer<typeof comparisonTableColumnSchema>, React.ReactNode>> =
  {
    price: <MoneyMd />,
    propertyType: <HomeMd />,
    bedrooms: <BedMd />,
    bathrooms: <BathMd />,
    carSpaces: <CarMd />,
    landSize: <LandSizeMd />,
    buildingArea: <FloorplanMd />,
    soldDate: <SoldMd />,
  };

const borderCellCss = css`
  border-right: 1px solid ${paletteHelper('borderSecondary')};

  &:last-child {
    border-right: none;
  }
`;

const cellPaddingCss = css`
  padding: ${spacingHelper('twoExtraSmall extraSmall')};
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  border-radius: ${radiusHelper('large')};
`;

const StyledTable = styled.table`
  border-radius: ${spacingHelper('large 0 0 large')};
  border-collapse: separate;
  display: block;
  white-space: nowrap;
  overflow-x: auto;
  margin-right: ${spacingHelper('medium')};
`;

const TableWrapper = styled.div`
  padding: 0 0 0 ${spacingHelper('medium')};
  background-color: white;
`;

const StyledCell = styled.td`
  min-width: 148px;
  width: 148px;
  white-space: normal;

  &:first-child {
    left: 100px;
  }
  ${cellPaddingCss}
  ${borderCellCss}
`;

const StyledCellHeader = styled.th`
  padding: ${spacingHelper('twoExtraSmall')};
  position: sticky;
  left: 0;
  z-index: 1;
  background: ${paletteHelper('backgroundPrimaryHigh')};
  vertical-align: middle;
  ${borderCellCss}
  ${cellPaddingCss}
`;

const StyledAddress = styled(Text)`
  font-weight: 600;
  white-space: normal;
`;

const StyledRow = styled.tr`
  /* stylelint-disable no-descending-specificity */
  td,
  th {
    &:first-child {
      border-left: 1px solid ${paletteHelper('borderSecondary')};
    }
    &:last-child {
      border-right: 1px solid ${paletteHelper('borderSecondary')};
    }
  }
  &:first-child {
    td,
    th {
      border-top: 1px solid ${paletteHelper('borderSecondary')};
      &:first-child {
        border-left: 1px solid ${paletteHelper('borderSecondary')};
        border-top-left-radius: ${spacingHelper('large')};
      }
      &:last-child {
        border-top-right-radius: ${spacingHelper('large')};
      }
    }
  }

  &:last-child {
    td,
    th {
      border-bottom: 1px solid ${paletteHelper('borderSecondary')};
      padding-bottom: ${spacingHelper('medium')};
      &:first-child {
        border-bottom-left-radius: ${spacingHelper('large')};
      }
      &:last-child {
        border-bottom-right-radius: ${spacingHelper('large')};
      }
    }
  }

  /* When there are only two properties, we want to make the width of 2 columns auto so it can fill the available space. */
  /* When there are more than 2 properties, we will set the fixed width of 148px for each column. */
  /* stylelint-disable-next-line plugin/no-unsupported-browser-features */
  &:has(> td:last-child:nth-child(3)) {
    td {
      width: auto;
    }
  }
  /* stylelint-enable no-descending-specificity */
`;

const Cell: FunctionComponent<{
  property: z.infer<typeof comparisonTableColumnSchema>;
  attributeKey: keyof z.infer<typeof comparisonTableColumnSchema>;
}> = ({ property, attributeKey }) => {
  if (attributeKey === 'imageUrl')
    return (
      <StyledCell key={`${property.propertyId}-${attributeKey}`}>
        <Image src={property.imageUrl ?? placeholderSVG} alt="Property Image" />
      </StyledCell>
    );

  return (
    <StyledCell key={`${property.propertyId}-${attributeKey}`}>
      {attributeKey === 'address' ? (
        <StyledAddress variant="body02">{property[attributeKey]}</StyledAddress>
      ) : (
        <Text variant="body02">{property[attributeKey] ?? '-'}</Text>
      )}
    </StyledCell>
  );
};

export const ComparisonTable: FunctionComponent<{
  component: z.infer<typeof comparisonTableComponentSchema>;
}> = ({ component }) => {
  // Get the properties array from the new schema.
  const data = component.properties;

  return (
    <TableWrapper>
      <StyledTable>
        <tbody>
          {sortedComparisonTableKeys.map((key) => (
            <StyledRow key={`row-${key}`}>
              <StyledCellHeader>{headers[key]}</StyledCellHeader>
              {data.map((property) => (
                <Cell
                  key={`${property.propertyId}-${key}`}
                  property={property}
                  attributeKey={key}
                />
              ))}
            </StyledRow>
          ))}
        </tbody>
      </StyledTable>
    </TableWrapper>
  );
};
